import { useState, useMemo, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Plus,
  FolderPlus,
  Clock,
  Settings,
  Globe,
  AlertCircle,
  CheckCircle2,
  Undo2,
  Redo2,
} from 'lucide-react';
import {
  DndContext,
  PointerSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import type { TodoItemResponse, TodoSectionResponse } from '@/lib/api';
import { useAuth } from '@/auth/hooks/useAuth';
import { useHolderSwitcher } from '@/components/HolderSwitcher';
import {
  useTodoList,
  useTodoSections,
  useTodoItems,
  useTodoUndoRedo,
  useTodoShortcuts,
} from '../hooks';
import { useListDetailHandlers } from '../hooks/useListDetailHandlers.js';
import {
  SectionCard,
  TodoItemRow,
  CreateSectionDialog,
  CreateTimeframeDialog,
  CreateItemDialog,
  EditSectionDialog,
  EditItemDialog,
  ListSettingsDialog,
} from '../components';
import { TodoListProvider } from '../context';
import type { TodoListContextValue } from '../context';

export function ListDetailPage() {
  const { listId } = useParams<{ listId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { selectedHolderId, isLoading: switcherLoading } = useHolderSwitcher();

  const { list, loading, error } = useTodoList(selectedHolderId, listId!);
  const {
    sections,
    loading: sectionsLoading,
    createSection,
    updateSection,
    deleteSection,
  } = useTodoSections(selectedHolderId, listId!);
  const {
    items: rawItems,
    loading: itemsLoading,
    createItem,
    updateItem,
    deleteItem,
    reorderItems,
  } = useTodoItems(selectedHolderId, listId!);

  // W6: "ownership" is enforced server-side by the wrapper's role gate.
  // From the page's perspective, if the user can read the list they can
  // also try to write — the server will 403 on insufficient role. For
  // Personal Holders the user is always the sole Principal (treat as
  // owner); for Org Holders the role is gated server-side.
  const isOwner = !!list && list.holderType === 'personal';
  const hasEditAccess = !!list; // server enforces; affords the controls

  const [listSettingsOpen, setListSettingsOpen] = useState(false);

  const {
    canUndo,
    canRedo,
    undo,
    redo,
    captureSnapshot,
    clear: clearUndoHistory,
  } = useTodoUndoRedo({ holderId: selectedHolderId, listId: listId!, enabled: hasEditAccess });

  useTodoShortcuts({ onUndo: undo, onRedo: redo });

  // Clear undo history if the list itself changes substantively (e.g.,
  // the user navigates to a different list with the same component
  // still mounted, or membership changes flip what they can see).
  const prevListKeyRef = useRef<string>('');
  useEffect(() => {
    const key = JSON.stringify({ id: list?.listId, holder: list?.holderId });
    if (prevListKeyRef.current && prevListKeyRef.current !== key) {
      clearUndoHistory();
    }
    prevListKeyRef.current = key;
  }, [list?.listId, list?.holderId, clearUndoHistory]);

  // Dialog state
  const [createSectionOpen, setCreateSectionOpen] = useState(false);
  const [createTimeframeOpen, setCreateTimeframeOpen] = useState(false);
  const [createItemOpen, setCreateItemOpen] = useState(false);
  const [editSectionOpen, setEditSectionOpen] = useState(false);
  const [editingSection, setEditingSection] = useState<TodoSectionResponse | null>(null);
  const [editItemOpen, setEditItemOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<TodoItemResponse | null>(null);

  // Extracted handlers
  const handlers = useListDetailHandlers(
    {
      holderId: selectedHolderId ?? '',
      listId: listId!,
      sections,
      items: rawItems,
      createSection,
      updateSection,
      deleteSection,
      createItem,
      updateItem,
      deleteItem,
      reorderItems,
      captureSnapshot,
      undo,
    },
    {
      setCreateSectionOpen,
      setCreateTimeframeOpen,
      setCreateItemOpen,
      setEditSectionOpen,
      setEditingSection,
      setEditItemOpen,
      setEditingItem,
    },
  );

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }),
  );

  // Derived data
  const activeItems = useMemo(() => rawItems.filter((i) => i.status !== 'completed'), [rawItems]);

  const topLevelSections = useMemo(
    () =>
      sections.filter((s) => s.parentSectionId === null).sort((a, b) => a.sortOrder - b.sortOrder),
    [sections],
  );

  const unsortedItems = useMemo(
    () => activeItems.filter((item) => item.sectionId === null),
    [activeItems],
  );

  const topLevelSectionIds = useMemo(
    () => topLevelSections.map((s) => s.sectionId),
    [topLevelSections],
  );

  const handleTopLevelDragEnd = useMemo(
    () => handlers.createTopLevelDragEnd(topLevelSections),
    [handlers, topLevelSections],
  );
  const handleUnsortedItemDragEnd = useMemo(
    () => handlers.createUnsortedItemDragEnd(unsortedItems),
    [handlers, unsortedItems],
  );

  // Context value
  const contextValue: TodoListContextValue = useMemo(
    () => ({
      isOwner,
      currentUserId: user?.userId,
      visibleSectionIds: undefined,
      onToggleItem: handlers.handleToggleItem,
      onEditItem: handlers.handleEditItem,
      onDeleteItem: handlers.handleDeleteItem,
      onEditSection: handlers.handleEditSection,
      onDeleteSection: handlers.handleDeleteSection,
      onCreateItem: handlers.handleCreateItem,
      onCreateSection: createSection,
      onReorderItems: (_sectionId, itemIds) => handlers.optimisticItemReorder(itemIds),
      onReorderSections: handlers.optimisticSectionReorder,
      onUpdateItemTitle: handlers.handleUpdateItemTitle,
      onCreateItemBelow: handlers.handleCreateItemBelow,
      onToggleHiddenFromPublic: handlers.handleToggleHiddenFromPublic,
    }),
    [isOwner, user?.userId, handlers, createSection],
  );

  if (switcherLoading || loading || sectionsLoading || itemsLoading) {
    return (
      <div className="max-w-4xl mx-auto p-6 space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-4 w-96" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!selectedHolderId) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <p className="text-sm text-muted-foreground">Select a workspace to view this list.</p>
      </div>
    );
  }

  if (error || !list) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <Card className="border-destructive">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-5 w-5" />
              Error
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">{error || 'List not found'}</p>
            <Button
              variant="outline"
              className="mt-4"
              onClick={() => navigate('/a/arche_todo_lists')}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to lists
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <TodoListProvider value={contextValue}>
      <div className="max-w-4xl mx-auto p-4 sm:p-6 space-y-6">
        {/* Header */}
        <div className="space-y-2">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/a/arche_todo_lists')}
              aria-label="Back to lists"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <h1 className="text-2xl font-semibold flex-1 min-w-0 truncate">{list.name}</h1>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {hasEditAccess && (
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={undo}
                  disabled={!canUndo}
                  aria-label="Undo"
                  title="Undo (Ctrl+Z)"
                >
                  <Undo2 className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={redo}
                  disabled={!canRedo}
                  aria-label="Redo"
                  title="Redo (Ctrl+Shift+Z)"
                >
                  <Redo2 className="h-4 w-4" />
                </Button>
              </div>
            )}
            <Link to={`/a/arche_todo_lists/${listId}/completed`}>
              <Button variant="outline" size="sm">
                <CheckCircle2 className="h-4 w-4 mr-2" />
                Completed
              </Button>
            </Link>
            {hasEditAccess && (
              <Button variant="outline" size="sm" onClick={() => setListSettingsOpen(true)}>
                <Settings className="h-4 w-4 mr-2" />
                Settings
              </Button>
            )}
          </div>
          {list.description && <p className="text-sm text-muted-foreground">{list.description}</p>}
          {list.isPublic && list.publicSlug && (
            <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
              <Globe className="h-3.5 w-3.5 flex-shrink-0" />
              <span>Public link:</span>
              <Link
                to={`/a/arche_todo_lists/public/${list.publicSlug}`}
                className="text-primary hover:underline break-all"
              >
                /a/arche_todo_lists/public/{list.publicSlug}
              </Link>
            </div>
          )}
          {list.holderType === 'org' && (
            <p className="text-xs text-muted-foreground">
              Org workspace — team members with access via Vault Membership.
            </p>
          )}
        </div>

        {/* Action bar */}
        {hasEditAccess && (
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setCreateTimeframeOpen(true)}>
              <Clock className="h-4 w-4 mr-2" />
              Add Timeframe
            </Button>
            <Button variant="outline" size="sm" onClick={() => setCreateSectionOpen(true)}>
              <FolderPlus className="h-4 w-4 mr-2" />
              Add Section
            </Button>
            <Button variant="outline" size="sm" onClick={() => setCreateItemOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add Item
            </Button>
          </div>
        )}

        {/* Sections */}
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleTopLevelDragEnd}
        >
          <div className="space-y-4">
            <SortableContext items={topLevelSectionIds} strategy={verticalListSortingStrategy}>
              {topLevelSections.map((section) => (
                <SectionCard
                  key={section.sectionId}
                  section={section}
                  sections={sections}
                  items={activeItems}
                />
              ))}
            </SortableContext>

            {unsortedItems.length > 0 && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    Unsorted
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-1">
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleUnsortedItemDragEnd}
                  >
                    <SortableContext
                      items={unsortedItems.map((i) => i.itemId)}
                      strategy={verticalListSortingStrategy}
                    >
                      {unsortedItems.map((item) => (
                        <TodoItemRow key={item.itemId} item={item} canEdit={hasEditAccess} />
                      ))}
                    </SortableContext>
                  </DndContext>
                </CardContent>
              </Card>
            )}

            {topLevelSections.length === 0 && unsortedItems.length === 0 && (
              <div className="text-center py-12 text-muted-foreground">
                <p>This list is empty. Add sections or items to get started.</p>
              </div>
            )}
          </div>
        </DndContext>

        {/* Dialogs */}
        <CreateTimeframeDialog
          open={createTimeframeOpen}
          onClose={() => setCreateTimeframeOpen(false)}
          onCreate={handlers.handleCreateTimeframe}
        />
        <CreateSectionDialog
          open={createSectionOpen}
          onClose={() => setCreateSectionOpen(false)}
          onCreate={handlers.handleCreateSection}
          sections={sections}
        />
        <EditSectionDialog
          open={editSectionOpen}
          onClose={() => {
            setEditSectionOpen(false);
            setEditingSection(null);
          }}
          section={editingSection}
          sections={sections}
          onSave={handlers.handleSaveSection}
        />
        <EditItemDialog
          open={editItemOpen}
          onClose={() => {
            setEditItemOpen(false);
            setEditingItem(null);
          }}
          item={editingItem}
          sections={sections}
          onSave={handlers.handleSaveItem}
        />
        <CreateItemDialog
          open={createItemOpen}
          onClose={() => setCreateItemOpen(false)}
          onCreate={handlers.handleCreateItem}
          sections={sections}
        />
        {hasEditAccess && (
          <ListSettingsDialog
            open={listSettingsOpen}
            onClose={() => setListSettingsOpen(false)}
            list={list}
            onSave={handlers.handleSettingsSave}
            onDelete={async () => {
              await handlers.handleDeleteList();
              navigate('/a/arche_todo_lists');
            }}
          />
        )}
      </div>
    </TodoListProvider>
  );
}
